import { ReactNode, useMemo } from 'react';
import { ExpandedState, functionalUpdate, SortingState, VisibilityState } from '@tanstack/react-table';
import { tableHelpers, VirtualizedTable } from '@round/creatorbase-ui';
import { Column, formatCount, formatEngagementRate, Meta, NO_VALUE, OutreachResultRow, PAGE_SIZE } from '../../helpers';
import { CreatorCell } from './components/CreatorCell/CreatorCell';
import { LocationCell } from './components/LocationCell/LocationCell';
import { RateCell } from './components/RateCell/RateCell';
import { ActionsCell } from './components/ActionsCell/ActionsCell';
import { TagsCell } from './components/TagsCell/TagsCell';
import { TagsHeader } from './components/TagsCell/TagsHeader';
import { SelectAllResultsHeader, SelectResultCell } from './components/SelectCell/SelectCell';
import { ExpandedResultContent } from './components/ExpandedResultContent/ExpandedResultContent';
import styles from './OutreachResultsTable.module.css';

const TruncatedCell = tableHelpers.TruncatedCell;
const SkeletonCell = tableHelpers.SkeletonCell;

const ROW_HEIGHT_PX = 56;

type Props = {
    rows: OutreachResultRow[];
    meta: Meta;
    columnVisibility: VisibilityState;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    fetchNextPage: () => void;
    noDataLabel: ReactNode;
    /** The result whose row is expanded to show its TikTok embed, or `null` if none is. */
    expandedResultId?: number | null;
    onRowClick?: (row: OutreachResultRow) => void;
};

export const OutreachResultsTable = ({
    rows,
    meta,
    columnVisibility,
    sorting,
    onSortingChange,
    fetchNextPage,
    noDataLabel,
    expandedResultId,
    onRowClick,
}: Props) => {
    const columns: Column[] = useMemo(
        () => [
            {
                header: SelectAllResultsHeader,
                id: 'select',
                cell: SelectResultCell,
                meta: { className: styles.selectCell },
                size: 48,
                enableSorting: false,
            },
            {
                header: 'Creator',
                id: 'creator',
                cell: CreatorCell,
                meta: { className: styles.creatorCell },
                size: 240,
                enableSorting: false,
            },
            {
                header: 'Followers',
                id: 'follower_count',
                accessorFn: (row) => formatCount(row.follower_count),
                cell: SkeletonCell,
                meta: { className: styles.flexCell },
                size: 120,
            },
            {
                header: 'Engagement rate',
                id: 'engagement_rate',
                accessorFn: (row) => formatEngagementRate(row.engagement_rate),
                cell: SkeletonCell,
                meta: { className: styles.flexCell },
                size: 150,
            },
            {
                header: 'Avg views',
                id: 'avg_views',
                accessorFn: (row) => formatCount(row.avg_views),
                cell: SkeletonCell,
                meta: { className: styles.flexCell },
                size: 120,
            },
            {
                header: 'Location',
                id: 'location',
                cell: LocationCell,
                meta: { className: styles.flexCell },
                size: 160,
                enableSorting: false,
            },
            {
                header: 'Email',
                id: 'email',
                accessorFn: (row) => row.email || NO_VALUE,
                cell: TruncatedCell,
                meta: { className: styles.flexCell },
                size: 220,
                enableSorting: false,
            },
            {
                header: 'Rate',
                id: 'rate',
                cell: RateCell,
                meta: { className: styles.flexCell },
                size: 120,
                enableSorting: false,
            },
            {
                header: TagsHeader,
                id: 'content_tags',
                cell: TagsCell,
                meta: { className: styles.flexCell },
                size: 200,
                enableSorting: false,
            },
            {
                // Nothing to head a column of buttons with, but the table still has to name it.
                header: () => <span className="sr-only">Actions</span>,
                id: 'actions',
                cell: ActionsCell,
                size: 160,
                enableSorting: false,
            },
        ],
        []
    );

    const data = tableHelpers.getLoadingRows(rows, { pageSize: PAGE_SIZE, isLoading: meta.isLoading });

    // Expanding is driven off the result's own id rather than tanstack's row id, since the row a
    // client last expanded is what has to stay expanded across a resort, not whichever index the id
    // now falls on.
    const expandedRowIndex = rows.findIndex((row) => row.id === expandedResultId);
    const expanded: ExpandedState = expandedRowIndex === -1 ? {} : { [expandedRowIndex]: true };

    return (
        <VirtualizedTable
            columns={columns}
            data={data}
            meta={meta}
            columnVisibility={columnVisibility}
            virtualizerOptions={{
                estimateSize: () => ROW_HEIGHT_PX,
                getItemKey: (index) => data[index]?.id ?? index,
            }}
            onEndReached={fetchNextPage}
            enableSorting
            manualSorting
            enableMultiSort={false}
            sorting={sorting}
            onSortingChange={(newSorting) => onSortingChange(functionalUpdate(newSorting, sorting))}
            enableExpanding
            expanded={expanded}
            getRowCanExpand={(row) => !tableHelpers.isLoadingRow(row.original)}
            renderSubComponent={(row) =>
                tableHelpers.isLoadingRow(row.original) ? null : (
                    <ExpandedResultContent result={row.original} columnVisibility={columnVisibility} />
                )
            }
            containerClassName={styles.tableContainer}
            onRowClick={
                onRowClick &&
                ((row) => {
                    if (!tableHelpers.isLoadingRow(row.original)) {
                        onRowClick(row.original);
                    }
                })
            }
            noDataLabel={noDataLabel}
        />
    );
};
